import {
  type BrushStroke,
  type StrokeData,
  strokeDrawing,
} from "./bishun-drawing.js";
import { setBrush } from "./bishun-playback.js";

/**
 * The drawing layer of a drill square: one character's 笔顺 drawing, with
 * every stroke unwritten, the guide paths the cue is pointed out on, and a
 * second brush per stroke for showing the reader a stroke being written.
 *
 * **The second brush is the demonstration.** The first is the reader's,
 * written as each stroke is taken, in the ink. The second writes the stroke
 * wanted next in the cue's blue, as the cue in the first stage and after a
 * stroke that was not taken in the first three. It is clipped to the same
 * outline as the first, through the same `clipPath`, so the two are the same
 * shape in two colours.
 *
 * **Every drawing's clip paths get ids of their own.** `bishun-drawing.ts`
 * names them `cb-stroke-0` and on, and a 词典 entry's stroke figure above the
 * drill is the same character under the same ids. A `url(#…)` reference takes
 * the first element in the page with that id, so the drill's strokes were
 * clipped to the figure's paths. From the third stage the stylesheet hides the
 * figure, Chrome and Firefox then clip to nothing, and every stroke snapped
 * into place was invisible. WebKit, and so every browser on an iPhone, drew
 * them anyway. Found on 2026-10-15.
 *
 * `xiezi-square.ts` puts one of these under the pad, and swaps it for another
 * character's when an Exercise moves on. The drawing is the one
 * `bishun-drawing.ts` builds for the entry's figure.
 */

/** A character's drawing, and the parts of it the square drives. */
export interface CharacterLayer {
  readonly drawing: Element;

  /** The brush for each stroke, in writing order. */
  readonly strokes: readonly BrushStroke[];

  /** The outline path for each stroke, in the same order. */
  readonly guides: readonly Element[];

  /** The demonstration brush for each stroke, in the same order. */
  readonly demos: readonly BrushStroke[];
}

const svgNamespace = "http://www.w3.org/2000/svg";

/** How many drawings this page has made, which numbers each one's ids. */
let drawingsMade = 0;

/**
 * Rename every clip path in a drawing to an id no other drawing on the page
 * has, and point the drawing's references at the new ids.
 */
function ownClipIds(drawing: Element): void {
  drawingsMade += 1;

  for (const clip of drawing.querySelectorAll("clipPath")) {
    const id = `${clip.id}-xiezi-${String(drawingsMade)}`;

    for (const clipped of drawing.querySelectorAll(
      `[clip-path="url(#${clip.id})"]`,
    )) {
      clipped.setAttribute("clip-path", `url(#${id})`);
    }

    clip.id = id;
  }
}

/**
 * A character's drawing, with nothing written yet.
 * @param document - The document the drawing is made in.
 * @param data - The character's outlines.
 * @returns The layer.
 */
export function characterLayer(
  document: Document,
  data: StrokeData,
): CharacterLayer {
  const { drawing, strokes } = strokeDrawing(document, data, "");

  ownClipIds(drawing);

  for (const stroke of strokes) {
    setBrush(stroke, false);
  }

  return {
    drawing,
    strokes,
    guides: [...drawing.querySelectorAll(".cidian-bishun-guide path")],
    demos: demoBrushes(document, drawing, strokes),
  };
}

/**
 * A copy of each stroke's brush in a group of its own over the ink, each
 * unwritten and clipped as the original is.
 *
 * Not `cidian-bishun-brush`, so that nothing reading the reader's strokes off
 * the drawing reads these as well. `modules/cidian-xiezi` gives them the
 * brush's width and transition.
 */
function demoBrushes(
  document: Document,
  drawing: Element,
  strokes: readonly BrushStroke[],
): readonly BrushStroke[] {
  const ink = drawing.querySelector(".cidian-bishun-ink");
  const group = document.createElementNS(svgNamespace, "g");

  group.setAttribute("class", "cidian-xiezi-demos");
  ink?.after(group);

  return strokes.map((stroke) => {
    const clipped = document.createElementNS(svgNamespace, "g");
    const brush = document.createElementNS(svgNamespace, "path");
    const clip = stroke.brush.parentElement?.getAttribute("clip-path");

    if (clip !== null && clip !== undefined) {
      clipped.setAttribute("clip-path", clip);
    }

    brush.setAttribute("class", "cidian-xiezi-demo");
    brush.setAttribute("d", stroke.brush.getAttribute("d") ?? "");
    clipped.append(brush);
    group.append(clipped);

    const demo = { brush, length: stroke.length };

    setBrush(demo, false);

    return demo;
  });
}
